import { useState, useEffect, useCallback } from 'react';

const DISMISS_KEY = 'ccc-install-dismiss-count';
const DISMISS_TS_KEY = 'ccc-install-dismiss-ts';
const NEVER_ASK_KEY = 'ccc-install-never-ask';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissCount, setDismissCount] = useState(0);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsInstalled(!!standalone);

    // Check if iOS
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    // Check never-ask
    if (localStorage.getItem(NEVER_ASK_KEY) === 'true') return;
    if (standalone) return;

    // Check cooldown
    const lastDismiss = parseInt(localStorage.getItem(DISMISS_TS_KEY) || '0', 10);
    if (Date.now() - lastDismiss < COOLDOWN_MS) return;

    const count = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    setDismissCount(count);

    // Listen for beforeinstallprompt (Chrome, Edge, Samsung)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after a delay if not installed
    if (ios && !standalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setIsInstalled(true);
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const dismiss = useCallback((neverAskAgain = false) => {
    const newCount = dismissCount + 1;
    localStorage.setItem(DISMISS_KEY, String(newCount));
    localStorage.setItem(DISMISS_TS_KEY, String(Date.now()));
    if (neverAskAgain) {
      localStorage.setItem(NEVER_ASK_KEY, 'true');
    }
    setDismissCount(newCount);
    setShowPrompt(false);
  }, [dismissCount]);

  return { showPrompt, isInstalled, isIOS, dismissCount, install, dismiss, canInstall: !!deferredPrompt };
}
